import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Facebook, Twitter, Instagram, Youtube, Linkedin, Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useSettings } from '../../context/SettingsContext';

const Footer = () => {
  const { settings } = useSettings();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');

  const visibleCategories = (settings.categories || []).filter((c) => c.visible);
  const social = settings.social || {};
  const siteTitle = settings.site_title || "Haber Türkiye'den";
  const [mainWord, ...rest] = siteTitle.split(' ');
  const tagline = settings.site_tagline || rest.join(' ');

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (email.trim()) {
      toast.success('Bültenimize başarıyla abone oldunuz!');
      setEmail('');
    }
  };

  // Social link config: only render rows with a URL
  const socialLinks = [
    { key: 'facebook', url: social.facebook, Icon: Facebook, hoverClass: 'hover:bg-blue-600' },
    { key: 'twitter', url: social.twitter, Icon: Twitter, hoverClass: 'hover:bg-sky-500' },
    { key: 'instagram', url: social.instagram, Icon: Instagram, hoverClass: 'hover:bg-pink-600' },
    { key: 'youtube', url: social.youtube, Icon: Youtube, hoverClass: 'hover:bg-red-600' },
    { key: 'linkedin', url: social.linkedin, Icon: Linkedin, hoverClass: 'hover:bg-blue-700' },
  ].filter((s) => s.url);

  return (
    <footer data-testid="main-footer" className="bg-[#0A0F1A] text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Günlük Bültenimize Abone Olun</h3>
            <p className="text-gray-400 text-sm mb-6">
              Ekonomi, finans ve iş dünyasından en önemli gelişmeleri her sabah e-postanızda bulun.
            </p>
            <form onSubmit={handleNewsletter} className="flex gap-2 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="E-posta adresiniz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-red-500 rounded-full"
                required
              />
              <Button type="submit" className="bg-red-600 hover:bg-red-700 rounded-full px-6">
                <Send className="w-4 h-4 mr-2" />
                Abone Ol
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2">
            <div className="mb-4">
              {settings.logo_data ? (
                <img
                  src={settings.logo_data}
                  alt={siteTitle}
                  className="h-12 w-auto object-contain brightness-0 invert"
                />
              ) : (
                <div className="flex flex-col">
                  <span className="text-2xl font-black tracking-tight leading-none uppercase">{mainWord}</span>
                  {tagline && (
                    <span className="text-xs font-bold text-red-500 tracking-[0.2em] uppercase">{tagline}</span>
                  )}
                </div>
              )}
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
              {settings.meta_description}
            </p>
            {/* Social Media */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3">
                {socialLinks.map(({ key, url, Icon, hoverClass }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${key}`}
                    aria-label={key}
                    className={`w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center transition-colors ${hoverClass}`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Haberler — dynamic from settings */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide mb-4 text-white">Haberler</h4>
            <ul className="space-y-2.5">
              {visibleCategories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/kategori/${c.slug}`}
                    className="text-gray-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-cat-${c.slug}`}
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Özel Bölümler */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide mb-4 text-white">Özel Bölümler</h4>
            <ul className="space-y-2.5">
              <li><Link to="/vizyon-sektor" className="text-red-500 hover:text-red-400 text-sm font-medium transition-colors">Vizyon Sektör</Link></li>
              <li><span className="text-gray-400 text-sm">Piyasa Analizi</span></li>
              <li><span className="text-gray-400 text-sm">Röportajlar</span></li>
              <li><span className="text-gray-400 text-sm">Köşe Yazıları</span></li>
              <li><span className="text-gray-400 text-sm">Araştırma</span></li>
            </ul>
          </div>

          {/* Kurumsal */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide mb-4 text-white">Kurumsal</h4>
            <ul className="space-y-2.5">
              <li><Link to="/hakkimizda" data-testid="footer-link-hakkimizda" className="text-gray-400 hover:text-white text-sm transition-colors">Hakkımızda</Link></li>
              <li><Link to="/reklam-isbirligi" data-testid="footer-link-reklam-isbirligi" className="text-gray-400 hover:text-white text-sm transition-colors">Reklam ve İş Birliği</Link></li>
              <li><Link to="/iletisim" data-testid="footer-link-iletisim" className="text-gray-400 hover:text-white text-sm transition-colors">İletişim</Link></li>
              <li><span className="text-gray-400 text-sm">Kariyer</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs">
              © {currentYear} {siteTitle}. Tüm hakları saklıdır.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-gray-500 hover:text-gray-400 text-xs cursor-pointer">Gizlilik Politikası</span>
              <span className="text-gray-500 hover:text-gray-400 text-xs cursor-pointer">Kullanım Koşulları</span>
              <span className="text-gray-500 hover:text-gray-400 text-xs cursor-pointer">Çerez Politikası</span>
              <span className="text-gray-500 hover:text-gray-400 text-xs cursor-pointer">KVKK</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
